import { CareServiceId } from '@app/icare/entities/careService/schema/CareService'
import { ClinicId } from '@app/icare/entities/clinic/schema/Clinic'
import { CostCarrierId } from '@app/icare/entities/costCarrier/schema/CostCarrier'
import { DoctorId } from '@app/icare/entities/doctor/schema/Doctor'
import { HospiceId } from '@app/icare/entities/hospice/schema/Hospice'
import { MedicalSupplyHomeId } from '@app/icare/entities/medicalSupplyHome/schema/MedicalSupplyHome'
import { NursingHomeId } from '@app/icare/entities/nursingHome/schema/NursingHome'
import { PharmacyId } from '@app/icare/entities/pharmacy/schema/Pharmacy'
import { msg } from '@lingui/core/macro'
import { entityId } from '@pkg/db/schema'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import {
  FaBriefcaseMedical,
  FaHospital,
  FaHouseChimneyMedical,
  FaHouseMedicalFlag,
  FaHouseUser,
  FaPerson,
  FaStaffSnake,
  FaTruckMedical,
  FaUserDoctor,
} from 'react-icons/fa6'
import { z } from 'zod'
import { PatientRelation } from './PatientRelation'

export const PatientApplicantEntityTypeEnum = defineEnum({
  clinic: { label: msg`Klinik`, icon: FaHospital },
  hospice: { label: msg`Hospiz`, icon: FaHouseMedicalFlag },
  pharmacy: { label: msg`Apotheke`, icon: FaStaffSnake },
  costCarrier: { label: msg`Kostenträger`, icon: FaBriefcaseMedical },
  careService: { label: msg`Pflegedienst`, icon: FaTruckMedical },
  doctor: { label: msg`Arzt`, icon: FaUserDoctor },
  medicalSupplyHome: { label: msg`Sanitätshaus`, icon: FaHouseChimneyMedical },
  nursingHome: { label: msg`Pflegeheim`, icon: FaHouseUser },
})

export type PatientApplicantEntityType = typeof PatientApplicantEntityTypeEnum.$.Type

export const PatientApplicantTypeEnum = PatientApplicantEntityTypeEnum.$.extend({
  PATIENT_OR_RELATIVE: {
    label: msg`Patient oder Angehöriger`,
    icon: FaPerson,
    description: msg`Patient selbst oder ein Angehöriger`,
  },
})

export type PatientApplicantType = typeof PatientApplicantTypeEnum.$.Type

export const isApplicantPatientOrRelative = (
  val: PatientApplicantRelation,
): val is z.infer<typeof PatientApplicationPatientOrRelative> =>
  val?.type === PatientApplicantTypeEnum.PATIENT_OR_RELATIVE

export const PatientApplicantId = entityId('patientApplicant')

export type PatientApplicantIdType = typeof PatientApplicantId.Type

export const ApplicantAwareFromEnum = defineEnum({
  searchEngine: msg`Suchmaschine`,
  socialMedia: msg`Soziale Medien`,
  recommendation: msg`Empfehlung`,
  other: msg`Sonstiges`,
})

export type ApplicantAwareFromType = typeof ApplicantAwareFromEnum.$.Type

const PatientApplicationPatientOrRelative = PatientRelation.extend({
  id: PatientApplicantId.schema,
  type: z.literal(PatientApplicantTypeEnum.PATIENT_OR_RELATIVE),
  awareFrom: z.enum(ApplicantAwareFromEnum.$.zodEnum()).nullish(),
})

export const PatientApplicantRelationSchema = z.discriminatedUnion('type', [
  PatientApplicationPatientOrRelative,
  PatientRelation.extend({
    id: PatientApplicantId.schema,
    type: z.literal(PatientApplicantTypeEnum.careService),
    toId: CareServiceId.ref,
  }),
  PatientRelation.extend({
    id: PatientApplicantId.schema,
    type: z.literal(PatientApplicantTypeEnum.doctor),
    toId: DoctorId.ref,
  }),
  PatientRelation.extend({
    id: PatientApplicantId.schema,
    type: z.literal(PatientApplicantTypeEnum.clinic),
    toId: ClinicId.ref,
  }),
  PatientRelation.extend({
    id: PatientApplicantId.schema,
    type: z.literal(PatientApplicantTypeEnum.hospice),
    toId: HospiceId.ref,
  }),
  PatientRelation.extend({
    id: PatientApplicantId.schema,
    type: z.literal(PatientApplicantTypeEnum.pharmacy),
    toId: PharmacyId.ref,
  }),
  PatientRelation.extend({
    id: PatientApplicantId.schema,
    type: z.literal(PatientApplicantTypeEnum.costCarrier),
    toId: CostCarrierId.ref,
  }),
  PatientRelation.extend({
    id: PatientApplicantId.schema,
    type: z.literal(PatientApplicantTypeEnum.medicalSupplyHome),
    toId: MedicalSupplyHomeId.ref,
  }),
  PatientRelation.extend({
    id: PatientApplicantId.schema,
    type: z.literal(PatientApplicantTypeEnum.nursingHome),
    toId: NursingHomeId.ref,
  }),
])

export type PatientApplicantRelation = z.infer<typeof PatientApplicantRelationSchema>
